"use client";
import { MessagesSquare } from "lucide-react";

import PageContainer from "@/app/admin/_common/PageContainer";
import PageHeader from "@/app/admin/_common/PageHeader";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

import { useCommunicationsModel } from "./communicationsPageModel";
import CommunicationsTable from "./_cmp/CommunicationsTable";
import CommunicationsTableSkeleton from "./_cmp/CommunicationsTableSkeleton";
import CommunicationsToolbar from "./_cmp/CommunicationsToolbar";
import CommunicationsKpiRow from "./_cmp/CommunicationsKpiRow";
import MessageDetailDrawer from "./_cmp/MessageDetailDrawer";

export default function CommunicationsPage() {
  const isMobile = useIsMobile();
  const {
    messages,
    stats,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    search,
    setSearch,
    channelFilter,
    setChannelFilter,
    statusFilter,
    setStatusFilter,
    sourceFilter,
    setSourceFilter,
    hasFilters,
    clearFilters,
    selected,
    setSelected,
  } = useCommunicationsModel();

  const toolbar = (
    <CommunicationsToolbar
      search={search}
      onSearchChange={setSearch}
      channelFilter={channelFilter}
      onChannelFilterChange={setChannelFilter}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      sourceFilter={sourceFilter}
      onSourceFilterChange={setSourceFilter}
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
    />
  );

  const table = isLoading ? (
    <CommunicationsTableSkeleton />
  ) : (
    <CommunicationsTable
      messages={messages}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      onLoadMore={loadMore}
      onSelect={setSelected}
      hasFilters={hasFilters}
    />
  );

  const drawer = (
    <MessageDetailDrawer message={selected} onClose={() => setSelected(null)} />
  );

  // Deliberately not a count of loaded rows: the feed is cursor-paged, so "50
  // messages" would just mean "one page" and would be wrong the moment you scroll.
  const subtitle = "Every email and text the system has sent";

  if (isMobile) {
    return (
      <PageContainer flush>
        <div className="flex h-full w-full flex-col overflow-y-auto">
          <div className="sticky top-0 z-10 flex flex-col gap-2 border-b border-border bg-card/80 px-3 py-2 backdrop-blur-sm">
            <PageHeader
              title="Communications"
              icon={MessagesSquare}
              subtitle={subtitle}
              className="static bg-transparent p-0 backdrop-blur-none"
            />
            {toolbar}
          </div>

          <div className="flex flex-1 flex-col pb-3">{table}</div>
        </div>
        {drawer}
      </PageContainer>
    );
  }

  return (
    <PageContainer flush>
      <div className="flex h-full w-full flex-col">
        <PageHeader title="Communications" icon={MessagesSquare} subtitle={subtitle} />

        <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 py-5">
          <CommunicationsKpiRow stats={stats} />
          {toolbar}

          <div className="flex min-h-0 flex-1 flex-col">{table}</div>
        </div>
      </div>
      {drawer}
    </PageContainer>
  );
}
